import { WmNavigator } from "@watermarkinsights/ripple-react";
import "./Header.scss";
import { initiateGainsight } from "./../../../static/js/gainsight_integration.js";
import { toggleNavOrgMgt } from "../../../static/js/commons";
import translate from "@/i18n/translate";
import { useIntl } from "react-intl";
import { useEffect, useReducer, useContext } from "react";
import { sendRequest } from "../../../common/http";
import { AppContext, AppContextInterface } from "../../../contexts/AppContext";
import {
  Institution,
  UserInstitution,
  MembershipInterface,
  InstitutionInterface
} from "../interfaces/Interface";

const { API_ROOT } = runConfig;

interface AppBarAction {
  type: string;
  membership: MembershipInterface;
}

interface AppBarState {
  membership: MembershipInterface | null;
}

const reducer = (state: AppBarState, action: AppBarAction) => {
  switch (action.type) {
    case "SetMemberShip":
      return { ...state, membership: action.membership };
    default:
      return state;
  }
};
export interface AppBarProps {
  institution: Institution;
}
const openHelpCentreLink = () => {
  const params = ["view=planning"];
  return window.open(`${API_ROOT}/zendesk/login?${params.join("&")}`, "_blank");
};

export default function AppBar({ institution }: AppBarProps): JSX.Element {
  const initialState = {
    membership: null
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const { setMembership, setComponentLoaded } =
    useContext<AppContextInterface>(AppContext);

  useEffect(() => {
    let orgParam = "";
    if (institution.organization && institution.organization.platformUuid) {
      orgParam =
        "?organization_platform_uuid=" + institution.organization.platformUuid;
    }
    const applyResult = (result: MembershipInterface) => {
      setMembership(result);
      setComponentLoaded("membership");
      dispatch({ type: "SetMemberShip", membership: result });
    };
    sendRequest({ uri: "/membership" + orgParam, camelize: true }, applyResult);
  }, []);

  useEffect(() => {
    if (state.membership != null && institution.gainsightEnabled) {
      initiateGainsight(state.membership.gainsightData);
    }
  }, [state.membership]);

  const headerBody = () => {
    const renderNavBar = () => {
      if (state.membership && state.membership.userRole === "admin") {
        return (
          <button
            id="mobile-nav-toggle"
            className="icononly hamburger-show"
            onClick={toggleNavOrgMgt}
          >
            <span className="sr-only tooltip -left">
              {translate("app.showNavigation")}
            </span>
            <span className="hamburger-inner"></span>
          </button>
        );
      }
    };

    return (
      <header className="top-bar topbar-overlay" role={"banner"}>
        {state.membership && renderNavBar()}
        <h1 className="logo">
          <img src="/images/planning-self-study.svg" alt="Watermark" />
        </h1>
        {state.membership && (
          <>
            <NavigationMenu
              institution={institution}
              membership={state.membership}
            />
          </>
        )}
      </header>
    );
  };

  return <>{headerBody()}</>;
}

export interface NavigationMenuProps {
  institution: Institution;
  membership: MembershipInterface;
}

const NavigationMenu = ({ institution, membership }: NavigationMenuProps) => {
  const { institutionsList } =
    useContext<AppContextInterface>(AppContext);

  if (membership.authType !== "off") {
    return (
      <div className="menus" role="application">
        <ul className="menu-bar nobullets">
          {institutionsList && institutionsList.length > 1 && (
            <li className="dropdown-select">
              <SelectedHierarchy institution={institution} />
              <InstitutionList
                institution={institution}
                institutionsList={institutionsList}
              />
            </li>
          )}
          <HelpCenter />
          <Navigator institution={institution} membership={membership} />
        </ul>
      </div>
    );
  } else {
    return (
      <div className="menus" role="application">
        <ul className="menu-bar nobullets">
          {institutionsList && institutionsList.length > 1 && (
            <li className="topbar-menu" tabIndex={-1}>
              <div
                id="hierarchy-select"
                className="dropdown-select prevent-header"
              >
                <SelectedHierarchy institution={institution} />
                <InstitutionList
                  institution={institution}
                  institutionsList={institutionsList}
                />
              </div>
            </li>
          )}
          <HelpCenter />
          <UserInfo membership={membership} />
        </ul>
      </div>
    );
  }
};
const HelpCenter = () => {
  const helpCenterlink = useIntl().formatMessage({
    id: "app.appBar.helpCenterlink"
  });
  return (
    <li className="products dropdown-select">
      <button
        className="header-help-icon"
        tabIndex={0}
        aria-label={helpCenterlink}
        onClick={openHelpCentreLink}
      >
        <i className="mdi mdi-help-circle"></i>
      </button>
    </li>
  );
};

const Navigator = (props: NavigationMenuProps) => {
  return (
    <li className="products dropdown-select">
      <WmNavigator
        id="navigator-example"
        user-name={props.membership.userName}
        email={props.membership.email}
        connection-name={props.membership.connectionName}
        auth-type={props.membership.authCode}
        products={props.membership.userModules}
        logoutUrl={props.membership.logoutUrl}
        mode="planning"
      ></WmNavigator>
    </li>
  );
};
interface HierarchyProps {
  institution: Institution;
}

const SelectedHierarchy = (props: HierarchyProps) => {
  const wmAccountName = useIntl().formatMessage(
    { id: "app.appBar.wmAccountName" },
    { institutionName: props.institution.name }
  );
  let institutionName = props.institution.name || <>&nbsp;</>;

  return (
    <button
      className="selectedoption button"
      id="oa-menu"
      tabIndex={0}
      aria-label={wmAccountName}
      onClick={() => window.wmrk.toggleHierarchySelect()}
      aria-expanded="false"
      aria-controls="oa-menu-listbox"
    >
      <span id="oa-menu-text" className="overflowcontrol prevent-header">
        {institutionName}
      </span>
    </button>
  );
};

interface InstitutionListProps {
  institution: Institution;
  institutionsList: UserInstitution[] | [];
}

const InstitutionList = (props: InstitutionListProps) => {
  const { REDIRECT_URI } = runConfig;
  const selectInstitution = useIntl().formatMessage({
    id: "app.appBar.selectInstitution"
  });
  const handleSwitchHierarchy = (currentInstitutionPlatformUuid: string, institutionPlatformUuid: string) => {
    if (currentInstitutionPlatformUuid !== institutionPlatformUuid){
      const applyResult = (institutionUuid: string) => {
        window.location = REDIRECT_URI + "/switch_hierarchy/" + institutionUuid;
      };
      
        sendRequest(
          {
            uri: `/institution/${institutionPlatformUuid}`,
            camelize: true
          },
          applyResult
        );
      }
  };

  return (
    <ul
      id="oa-menu-listbox"
      className="options nobullets"
      role="listbox"
      aria-label={selectInstitution}
      aria-activedescendant={`${props.institution.platformUuid}`}
      tabIndex={0}
    >
      {props.institutionsList.map((i: UserInstitution) => {
        return (
          <li
            key={i.uuid}
            id={i.uuid}
            role="option"
            className={hierarchyClass(
              props.institution.platformUuid,
              i.uuid
            )}
            aria-selected={props.institution.platformUuid === i.uuid}
            onClick={() =>
              handleSwitchHierarchy(props.institution.platformUuid, i.uuid)
            }
          >
            {i.name}
          </li>
        );
      })}
    </ul>
  );
};
interface UserProps {
  membership: MembershipInterface;
}

const UserInfo = (props: UserProps) => {
  const userSetting = useIntl().formatMessage({
    id: "app.appBar.userSetting"
  });

  let logOutButton;
  if (props.membership.authType === "off") {
    logOutButton = (
      <li className="option" tabIndex={-1} onClick={() => wmrk.logout()}>
        {translate("app.appBar.signOut")}
      </li>
    );
  } else {
    logOutButton = (
      <li
        className="logout option"
        onClick={() => {
          return wmrk.goToLocation(props.membership.logoutUrl);
        }}
        id="logprod"
      >
        {translate("app.appBar.signOut")}
      </li>
    );
  }

  return (
    <li id="usersettings" className="topbar-menu" tabIndex={-1}>
      <div id="account-select" className="dropdown-select">
        <button
          id="account-select-link"
          className="username selectedoption prevent-header"
          aria-controls="acc-listbox"
          aria-expanded="false"
          tabIndex={0}
          onClick={() => wmrk.toggleAccountMenu()}
        >
          {props.membership.userName}
        </button>
        <ul
          id="acc-listbox"
          className="dropdown-menu options nobullets"
          aria-label={userSetting}
          tabIndex={-1}
        >
          <li
            id="user-menu-account"
            tabIndex={-1}
            className="option prevent-header"
            onClick={() => wmrk.goToProfile()}
          >
            {translate("app.appBar.account")}
          </li>
          {logOutButton}
        </ul>
      </div>
    </li>
  );
};

function hierarchyClass(
  loggedInInstitutionPlatformUuid: String,
  institutionPlatformUuid: String
) {
  const className = "option";
  if (loggedInInstitutionPlatformUuid === institutionPlatformUuid) {
    return className + " selected";
  }

  return className;
}
